import 'img-comparison-slider'
import moment from 'moment'
import { useUserStore } from '@/store/user'
import mockCapture1Original from '@/assets/images/ai-assistant/mock/capture-1-original.jpg'
import mockCapture1Heatmap from '@/assets/images/ai-assistant/mock/capture-1-heatmap.jpg'
import mockCapture2Original from '@/assets/images/ai-assistant/mock/capture-2-original.jpg'
import mockCapture3Original from '@/assets/images/ai-assistant/mock/capture-3-original.jpg'
import ExamAiAssistantPatientIntroVideo from './ExamAiAssistantPatientIntroVideo.vue'
import ExamAiAssistantPatientIntroWelcome from './ExamAiAssistantPatientIntroWelcome.vue'

const IS_PRODUCTION = process.env.NODE_ENV == 'production'

export const CLASS_TAGS_COUNT = {
  macularAlterations: 3,
  diabeticRetinopathy: 1,
  opticNerveAlterations: 3,
  vascularAlterations: 4,
  pigmentaryAlterations: 3,
  otherAlterations: 1,
}

export const PATIENT_ITEMS_COUNT = 3

export const CLASS_KEYS = [
  { tone: 'info', key: 'diabeticRetinopathy' },
  { tone: 'warning', key: 'opticNerveAlterations' },
  { tone: 'warning', key: 'macularAlterations' },
  { tone: 'warning', key: 'pigmentaryAlterations' },
  { tone: 'warning', key: 'otherAlterations' },
]

const CLASS_TONE_BY_KEY = Object.fromEntries(CLASS_KEYS.map(c => [c.key, c.tone]))

export const COMBINATION_KEYS = []
for (let i = 0; i < CLASS_KEYS.length; i++) {
  for (let j = i + 1; j < CLASS_KEYS.length; j++) {
    COMBINATION_KEYS.push({ tone: 'warning', combo: [CLASS_KEYS[i].key, CLASS_KEYS[j].key] })
  }
}

// TODO remove once findings come from the real AI metadata instead of this full-catalog mock.
const FINDING_KEYS = [
  ...CLASS_KEYS,
  ...COMBINATION_KEYS,
  { tone: 'success', key: 'normal' },
]

const MOCK_CAPTURE_ASSETS = [
  { original: mockCapture1Original, heatmap: mockCapture1Heatmap },
  { original: mockCapture2Original, heatmap: mockCapture1Heatmap },
  { original: mockCapture3Original, heatmap: mockCapture1Heatmap },
]
const MOCK_CAPTURES = FINDING_KEYS.map((_, index) => {
  const asset = MOCK_CAPTURE_ASSETS[index % MOCK_CAPTURE_ASSETS.length]
  return {
    id: `mock-${index + 1}`,
    isMock: true,
    original: { url: asset.original },
    heatmap: { url: asset.heatmap },
    imageLaterality: index % 2 === 0 ? 'R' : 'L',
  }
})

const METRICS = {
  diabeticRetinopathy: { sensitivity: '95%', specificity: '93%' },
  generalAlterations: { sensitivity: '85%', specificity: '95%' },
  opticNerve: { sensitivity: '75%' },
}

export default {
  name: 'ExamAiAssistantTab',
  components: {
    ExamAiAssistantPatientIntroVideo,
    ExamAiAssistantPatientIntroWelcome,
  },
  props: ['exam_id', 'patient'],
  data: () => ({
    loading: false,
    fetched: false,
    exam: null,
    session: null,
    examImages: [],
    dataPath: '',
    thumbDataPath: '',
    captures: [],
    activeIndex: 0,
    techInfoOpen: false,
    metrics: METRICS,
    isFullscreen: false,
    isPatientMode: false,
    // 0 = intro video, 1 = welcome/explainer screen, 2 = the real patient-facing finding
    patientModeStep: 0,
    suppressPatientModeExitOnce: false,
    isSideBySideMode: false,
    sideBySideActiveIndexLeft: 0,
    sideBySideActiveIndexRight: 0,
    loadedIds: new Set(),
    isDarkTheme: false,
  }),
  computed: {
    findings() {
      return FINDING_KEYS.map(f => f.combo
        ? this.buildCombinationFinding(f.combo[0], f.combo[1], f.tone)
        : this.buildFinding(f.key, f.tone))
    },
    activeFinding() {
      const keys = this.currentCaptureFindingKeys
      if (!keys)
        return this.currentCapture?.isMock ? (this.findings[this.activeIndex % this.findings.length] || null) : null

      if (keys.length === 0)
        return this.buildFinding('normal', 'success')

      if (keys.length === 1)
        return this.buildFinding(keys[0], CLASS_TONE_BY_KEY[keys[0]])

      return this.buildCombinationFinding(keys[0], keys[1], 'warning')
    },
    // null = no usable AI reading for this capture (fall back to the mock catalog).
    currentCaptureFindingKeys() {
      const metadataString = this.currentCapture?.original?.metadata || this.currentCapture?.heatmap?.metadata
      return this.$utils.getMcRASE2FindingKeys(metadataString)
    },
    isJokerPatient() {
      return this.patient?.uuid === this.session?.currentClinic?.jokerPatientUuid
    },
    patientName() {
      return this.isJokerPatient ? this.$t('noPatientSelected') : (this.patient?.fullName || '')
    },
    examDateFormatted() {
      return this.exam?.date ? moment.utc(this.exam.date).format(this.$t('dateFormat')) : ''
    },
    clinicName() {
      return this.session?.currentClinic?.name || ''
    },
    printObj() {
      return { id: 'ai-assistant-print-area', popTitle: this.patientName, closeCallback: this.onAfterPrint }
    },
    currentCapture() {
      return this.captures[this.activeIndex] || null
    },
    leftEyeCaptures() {
      return this.captures.filter(capture => capture.imageLaterality === 'L')
    },
    rightEyeCaptures() {
      return this.captures.filter(capture => capture.imageLaterality === 'R')
    },
    currentLeftEyeCapture() {
      return this.leftEyeCaptures[this.sideBySideActiveIndexLeft] || null
    },
    currentRightEyeCapture() {
      return this.rightEyeCaptures[this.sideBySideActiveIndexRight] || null
    },
    canSideBySide() {
      return this.leftEyeCaptures.length > 0 && this.rightEyeCaptures.length > 0
    },
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    this.fetchData()

    this.$nextTick(() => this.measureAvailableHeight())
    window.addEventListener('resize', this.measureAvailableHeight)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    window.addEventListener('keydown', this.handleCaptureStepKeys)

    this.isDarkTheme = document.documentElement.classList.contains('dark-theme')
    this.themeObserver = new MutationObserver(() => {
      this.isDarkTheme = document.documentElement.classList.contains('dark-theme')
    })
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
  },
  unmounted() {
    window.removeEventListener('resize', this.measureAvailableHeight)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    window.removeEventListener('keydown', this.handleCaptureStepKeys)
    this.themeObserver?.disconnect()
  },
  methods: {
    buildFinding(key, tone) {
      const base = `aiAssistant.findings.${key}`
      const tagsCount = CLASS_TAGS_COUNT[key] || 0

      return {
        tone,
        title: this.$t(`${base}.title`),
        cardDescription: this.$t(`${base}.cardDescription`),
        summary: this.$t(`${base}.summary`),
        tags: Array.from({ length: tagsCount }, (_, i) => this.$t(`${base}.tags.${i}`)),
        detailText: this.$t(`${base}.detailText`),
        doctorInterpretation: this.$t(`${base}.doctorInterpretation`),
        patient: this.buildPatientContent(`${base}.patient`),
      }
    },
    buildPatientContent(base) {
      return {
        heading: this.$t(`${base}.heading`),
        regionTitle: this.$t(`${base}.regionTitle`),
        regionText1: this.$t(`${base}.regionText1`),
        regionText2: this.$t(`${base}.regionText2`),
        meansItems: Array.from({ length: PATIENT_ITEMS_COUNT }, (_, i) => this.$t(`${base}.meansItems.${i}`)),
        doesNotItems: Array.from({ length: PATIENT_ITEMS_COUNT }, (_, i) => this.$t(`${base}.doesNotItems.${i}`)),
      }
    },
    buildCombinationFinding(key1, key2, tone) {
      const base = 'aiAssistant.findings.combination'
      const class1 = this.buildFinding(key1, tone)
      const class2 = this.buildFinding(key2, tone)

      const tags = []
      ;[...class1.tags, ...class2.tags].forEach(tag => {
        if (!tags.includes(tag) && tags.length < 6) tags.push(tag)
      })

      return {
        tone,
        title: this.$t(`${base}.title`),
        summary: this.$t(`${base}.summary`, { class1: class1.title.toLowerCase(), class2: class2.title.toLowerCase() }),
        classes: [
          { number: 1, title: class1.title, description: class1.cardDescription },
          { number: 2, title: class2.title, description: class2.cardDescription },
        ],
        tagsTitle: this.$t(`${base}.tagsTitle`),
        tags,
        detailText: this.$t(`${base}.detailText`, {
          conditionList: tags.join(', '),
          class1: class1.title.toLowerCase(),
          class1Detail: class1.detailText,
          class2: class2.title.toLowerCase(),
          class2Detail: class2.detailText,
        }),
        doctorInterpretation: this.$t(`${base}.doctorInterpretation`, { class1: class1.title, class2: class2.title }),
        patient: this.buildPatientContent(`${base}.patient`),
      }
    },
    measureAvailableHeight() {
      requestAnimationFrame(() => {
        if (!this.$el?.getBoundingClientRect) return
        this.$el.style.setProperty('--ai-assistant-tab-top', `${this.$el.getBoundingClientRect().top}px`)
      })
    },
    onThumbsWheel(event) {
      if (!event.deltaY) return
      event.currentTarget.scrollLeft += event.deltaY
      event.preventDefault()
    },
    onThumbsDragStart(event) {
      const el = event.currentTarget
      const startX = event.pageX
      const startScrollLeft = el.scrollLeft
      let dragged = false

      const onMove = moveEvent => {
        dragged = true
        el.classList.add('ai-assistant-tab__thumbs--dragging')
        el.scrollLeft = startScrollLeft - (moveEvent.pageX - startX)
      }
      const onUp = () => {
        el.classList.remove('ai-assistant-tab__thumbs--dragging')
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        if (dragged)
          el.addEventListener('click', suppressClick, { capture: true, once: true })
      }
      const suppressClick = clickEvent => {
        clickEvent.stopPropagation()
        clickEvent.preventDefault()
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    isLoaded(key) {
      return this.loadedIds.has(key)
    },
    markLoaded(key) {
      this.loadedIds.add(key)
    },
    onImgMounted(event, key) {
      if (event.el.complete)
        this.markLoaded(key)
    },
    onSliderMounted(event, key) {
      setTimeout(() => this.markLoaded(key), 500)
    },
    toggleFullscreen() {
      if (document.fullscreenElement)
        document.exitFullscreen()
      else
        this.$refs.viewer?.requestFullscreen()
    },
    togglePatientMode() {
      this.isPatientMode = !this.isPatientMode

      if (this.isPatientMode) {
        this.patientModeStep = 0
        this.$el?.requestFullscreen?.()
      } else if (document.fullscreenElement === this.$el) {
        document.exitFullscreen()
      }
    },
    toggleSideBySideMode() {
      this.isSideBySideMode = !this.isSideBySideMode

      if (this.isSideBySideMode)
        this.$refs.viewer?.requestFullscreen()
      else if (document.fullscreenElement === this.$refs.viewer)
        document.exitFullscreen()
    },
    prepareForPrint() {
      if (!document.fullscreenElement) return
      this.suppressPatientModeExitOnce = true
      document.exitFullscreen()
    },
    onAfterPrint() {
      if (this.isPatientMode && !document.fullscreenElement)
        this.isPatientMode = false
    },
    onFullscreenChange() {
      this.isFullscreen = document.fullscreenElement === this.$refs.viewer

      if (!document.fullscreenElement)
        this.measureAvailableHeight()

      if (!document.fullscreenElement && this.isPatientMode) {
        if (this.suppressPatientModeExitOnce)
          this.suppressPatientModeExitOnce = false
        else
          this.isPatientMode = false
      }

      if (!document.fullscreenElement && this.isSideBySideMode)
        this.isSideBySideMode = false
    },
    async fetchData() {
      this.loading = true
      NProgress.start()
      try {
        const examResponse = await this.$examService.get({ id: this.exam_id })
        this.exam = examResponse.exam

        const response = await this.$examDataService.list({ id: this.exam.id })
        this.examImages = response.examDataList.filter(image => image.type !== 'RNFL_RAW')
        this.thumbDataPath = `${response.thumbDataPath}`
        this.dataPath = `${response.dataPath}`

        this.buildCaptures()

        // TODO move to service
        if (!IS_PRODUCTION) {
          this.thumbDataPath = `http://localhost:1337${response.thumbDataPath}`
          this.dataPath = `http://localhost:1337${response.dataPath}`
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
        this.fetched = true
      }
    },
    buildCaptures() {
      const originals = this.examImages.filter(image => image.type !== 'HEATMAP' && this.parseMetadata(image)?.aiMetadata?.mcRASE2)
      const heatmaps = this.examImages.filter(image => image.type === 'HEATMAP')

      const rawCaptures = originals.map(original => ({
        id: original.name,
        original,
        heatmap: this.findHeatmapFor(original, heatmaps) || null,
        imageLaterality: original.imageLaterality,
      }))

      this.captures = this.dropUnusableCaptures(rawCaptures)

      // TODO remove once real capture data is reliably available for every exam.
      if (!this.captures.length && !IS_PRODUCTION) {
        this.captures = MOCK_CAPTURES
      }
    },
    dropUnusableCaptures(captures) {
      return captures.filter(capture => this.hasUsableReading(capture))
    },
    hasUsableReading(capture) {
      return this.$utils.getMcRASE2FindingKeys(capture.original?.metadata) !== null
    },
    findHeatmapFor(original, heatmaps) {
      const childrenUUID = this.parseMetadata(original)?.childrenUUID || []
      return heatmaps.find(heatmap => {
        if (childrenUUID.includes(heatmap.name)) return true
        const parentsUUID = this.parseMetadata(heatmap)?.parentsUUID || []
        return parentsUUID.includes(original.name)
      })
    },
    parseMetadata(image) {
      if (!image?.metadata) return null
      try {
        return JSON.parse(image.metadata)
      } catch (err) {
        return null
      }
    },
    imageUrl(image, basePath) {
      if (!image) return ''
      return image.url || `${basePath}/${image.name}`
    },
    step(direction) {
      const total = this.captures.length
      if (!total) return
      this.activeIndex = (this.activeIndex + direction + total) % total
    },
    handleCaptureStepKeys(event) {
      if (this.isSideBySideMode || this.captures.length < 2 || this.isAnyOverlayOpen())
        return

      if (event.key === 'ArrowLeft')
        this.step(-1)
      else if (event.key === 'ArrowRight')
        this.step(1)
    },
    isAnyOverlayOpen() {
      const isLightGalleryOpen = document.querySelector('body .lg-container.lg-show.lg-show-in')
      const homeHeaderSearchBox = document.querySelector('header .search-box')
      const menuMobileSearchBox = document.querySelector('.menu-mobile-box .search-box')
      const isOffcanvasOpen = document.querySelector('.offcanvas-backdrop.fade.show')

      let isSearchBarOrFilterOpen = false

      if (homeHeaderSearchBox && (homeHeaderSearchBox.classList.contains('open') || homeHeaderSearchBox.classList.contains('expand')))
        isSearchBarOrFilterOpen = true

      if (menuMobileSearchBox && (menuMobileSearchBox.classList.contains('open') || menuMobileSearchBox.classList.contains('expand')))
        isSearchBarOrFilterOpen = true

      return !!isLightGalleryOpen || isSearchBarOrFilterOpen || !!isOffcanvasOpen
    },
  },
}
